import type { HTMLAttributes } from "react";

import { cn } from "../lib/cn";
import { useScrollReach } from "../lib/use-scroll-reach";

/**
 * A row of cards that scrolls sideways instead of wrapping.
 *
 * For a collection whose length is not known in advance — a clutch of four eggs or
 * seven, a season's mutations, however many rounds are running. Wrapped, those turn
 * one row into three or four and push everything below them off a phone screen; the
 * last row is usually ragged too, which reads as a layout mistake rather than as data.
 *
 * A fixed set that is designed to fit — the four totals tiles — stays a grid. Making
 * *that* scroll would hide a quarter of it behind an edge for no reason.
 *
 * The row fades at whichever end it can still be pulled from, so there is something to
 * see when the last card finishes flush with the screen and the row looks complete.
 *
 * Children must carry their own `shrink-0` and a width: a flex item with neither will
 * compress to fit rather than overflow, and the row will not scroll at all.
 */
export function ScrollRow({ className, children, ...props }: HTMLAttributes<HTMLUListElement>) {
  const { ref, fade } = useScrollReach<HTMLUListElement>(children);

  return (
    <ul
      ref={ref}
      className={cn(
        "flex snap-x gap-md overflow-x-auto pb-xs",
        // The row is a swipe affordance, not a control; the scrollbar under it would
        // be one more horizontal line on a screen made of stacked cards.
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        fade,
        className,
      )}
      {...props}
    >
      {children}
    </ul>
  );
}
